import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule } from '@angular/common';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnDestroy,
    OnInit,
    DOCUMENT,
    inject,
} from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Subscription } from 'rxjs';

@Component({
    selector: 'app-footer-language-translator',
    imports: [CommonModule, MatButtonModule, MatIconModule, MatTooltipModule],
    templateUrl: './app-footer-language-translator.component.html',
    styleUrls: ['./app-footer-language-translator.component.scss'],
})
export class AppFooterLanguageTranslatorComponent implements OnInit, AfterViewInit, OnDestroy {
    private bo = inject(BreakpointObserver);
    private cdr = inject(ChangeDetectorRef);
    private document = inject<Document>(DOCUMENT);
    private element = inject(ElementRef);

    private subs: Subscription[] = [];
    private static isGloballyInitializing = false;

    ngOnInit(): void {
        this.subs.push(
            this.bo.observe('(max-width: 768px)').subscribe(() => {
                /*
                 * KNOW-1204 The breakpoint observer must reset the Google Translate widget, as this subscription doesn't
                 * re-trigger ng lifecycle hooks, which can result in the Google Translate Widget not being rendered properly.
                 */
                this.cleanAndInitGoogleTranslateWidget();
                this.cdr.detectChanges();
            }),
        );
    }

    ngAfterViewInit(): void {
        this.cleanAndInitGoogleTranslateWidget();
    }

    private isInitializing = false;

    /**
     * Adds Google Translate Initialization scripts to the DOM,
     * only after cleaning any scripts with the same attribute ID.
     */
    private cleanAndInitGoogleTranslateWidget(): void {
        if (this.isInitializing || AppFooterLanguageTranslatorComponent.isGloballyInitializing) return;
        this.isInitializing = true;
        AppFooterLanguageTranslatorComponent.isGloballyInitializing = true;
        const prototypeId = 'gt-prototype';
        const invokeId = 'gt-invoke';

        // clean up any existing scripts
        [prototypeId, invokeId].forEach((id) => this.document.getElementById(id)?.remove());

        const v = this.document.createElement('script');
        v.id = prototypeId;
        v.type = 'text/javascript';

        // This list should match https://genesys-confluence.atlassian.net/wiki/spaces/GCK/pages/162661526/Languages+Supported+in+Knowledge+Services
        const languages = [
            'en',
            'es',
            'fr',
            'de',
            'it',
            'pt',
            'pt-PT',
            'nl',
            'ca',
            'tr',
            'sv',
            'no',
            'da',
            'fi',
            'ja',
            'ar',
            'ko',
            'zh-CN',
            'zh-TW',
            'pl',
            'th',
            'hi',
            'hu',
            'vi',
            'uk',
            'eu',
            'iw',
            'ru',
            'cs',
        ].join(',');

        const vFunctionName = 'googleTranslateElementInit';

        v.innerHTML = `function ${vFunctionName}() { new google.translate.TranslateElement({ pageLanguage: 'en', includedLanguages: '${languages}', layout: google.translate.TranslateElement.InlineLayout.SIMPLE }, 'google_translate_element'); }`;
        this.element.nativeElement.appendChild(v);

        const s = this.document.createElement('script');
        s.id = invokeId;
        s.type = 'text/javascript';
        s.src = `https://translate.google.com/translate_a/element.js?cb=${vFunctionName}`;
        this.element.nativeElement.appendChild(s);

        setTimeout(() => {
            this.isInitializing = false;
            AppFooterLanguageTranslatorComponent.isGloballyInitializing = false;
        }, 1000);
    }

    openGoogleTranslate(event?: Event): void {
        event?.stopPropagation();
        let el = this.document.getElementById('google_translate_element').children.item(0) as HTMLElement;
        el = el?.children?.item(0) as HTMLElement;
        el?.click();
    }

    ngOnDestroy(): void {
        this.subs?.forEach((sub) => sub?.unsubscribe());
    }
}
